import React, { useState, useEffect, useMemo, useCallback } from "react";
import { getAllCustomers } from "../../api/customerService";
import { getAllProducts } from "../../api/productService";
import { getAllSuppliers } from "../../api/supplierService";
import { getPaginatedSalesOrders } from "../../api/salesOrderService";
import Layout from "../../components/Layout";
import Sidebar from "../../components/Sidebar";
import ReportDownloader from "../../components/Reports/ReportDownloader";
import {
    BarChart2,
    Package,
    FileText,
    ClipboardList,
    Users,
    Star,
    ChevronDown,
    CalendarDays,
    CalendarRange,
    ShoppingBag,
    UserCheck,
    TrendingUp,
    Receipt,
    CreditCard,
    Clock,
    AlertCircle,
    Layers,
    Timer,
    LayoutList,
    DollarSign,
    RotateCcw,
    ShieldAlert,
    Truck,
} from "lucide-react";
import "./Reports.css";

/* ── Date helpers ── */
const today = new Date().toLocaleDateString("en-CA");
const firstOfMonth = new Date(new Date().getFullYear(), new Date().getMonth(), 1)
    .toLocaleDateString("en-CA");
const lastOfMonth = new Date(new Date().getFullYear(), new Date().getMonth() + 1, 0)
    .toLocaleDateString("en-CA");

/* ── Year helpers ── */
const currentYear = new Date().getFullYear();
const YEAR_OPTIONS = Array.from(
    { length: currentYear - 2020 + 1 },
    (_, i) => ({ label: String(currentYear - i), value: String(currentYear - i) })
);

/* ─────────────────────────────────────────────────────────────────────
   Report definitions
   Each entry maps directly to ReportDownloader props.
   Add new reports here — no JSX changes needed.
───────────────────────────────────────────────────────────────────── */

// ── Section 3: Sales Reports ─────────────────────────────────────────
const SALES_REPORTS = [
    {
        reportType: "DAILY_SALES_REPORT",
        reportName: "Daily Sales Activity Report",
        description: "Gross sales, net sales and total invoices summary for a specific day.",
        icon: <CalendarDays size={22} />,
        params: [
            {
                key: "reportDate",
                label: "Date",
                type: "date",
                required: true,
                defaultValue: today,
            },
        ],
        // Convert single date → startDate + endDate (same day)
        beforeSubmit: (v) => ({ startDate: v.reportDate, endDate: v.reportDate }),
    },
    {
        reportType: "MONTHLY_SALES_REPORT",
        reportName: "Monthly Sales Activity Report",
        description: "Gross sales, net sales and total invoices summary for a selected month range.",
        icon: <CalendarRange size={22} />,
        params: [
            {
                key: "startDate",
                label: "From Date",
                type: "date",
                required: true,
                defaultValue: firstOfMonth,
            },
            {
                key: "endDate",
                label: "To Date",
                type: "date",
                required: true,
                defaultValue: lastOfMonth,
            },
        ],
    },
    {
        reportType: "INVOICE_WISE",
        reportName: "Invoice-wise Sales Report",
        description:
            "List of all approved invoices with customer and total amount, filtered by date range.",
        icon: <Receipt size={22} />,
        params: [
            {
                key: "startDate",
                label: "From Date",
                type: "date",
                required: true,
                defaultValue: firstOfMonth,
            },
            {
                key: "endDate",
                label: "To Date",
                type: "date",
                required: true,
                defaultValue: lastOfMonth,
            },
        ],
    },
    {
        reportType: "PRODUCT_WISE",
        reportName: "Product-wise Sales Report",
        description:
            "Revenue and quantity grouped by product for approved orders in the date range.",
        icon: <ShoppingBag size={22} />,
        params: [
            {
                key: "startDate",
                label: "From Date",
                type: "date",
                required: true,
                defaultValue: firstOfMonth,
            },
            {
                key: "endDate",
                label: "To Date",
                type: "date",
                required: true,
                defaultValue: lastOfMonth,
            },
        ],
    },
    {
        reportType: "CUSTOMER_WISE",
        reportName: "Customer-wise Sales Report",
        description:
            "Order count and total spend grouped by customer for approved orders.",
        icon: <UserCheck size={22} />,
        params: [
            {
                key: "startDate",
                label: "From Date",
                type: "date",
                required: true,
                defaultValue: firstOfMonth,
            },
            {
                key: "endDate",
                label: "To Date",
                type: "date",
                required: true,
                defaultValue: lastOfMonth,
            },
        ],
    },
    {
        reportType: "ANNUAL_PRODUCT_SALES",
        reportName: "Product-wise Sales Annual",
        description:
            "Monthly sales breakdown per product for an entire year — 12-column landscape report.",
        icon: <TrendingUp size={22} />,
        params: [
            {
                key: "year",
                label: "Year",
                type: "select",
                required: true,
                defaultValue: String(currentYear),
                options: YEAR_OPTIONS,
            },
        ],
    },
    {
        reportType: "DAILY_CUSTOMER_SUMMARY",
        reportName: "Customer-wise Sales Credit Details Summary",
        description: "Customer-wise total invoices, amounts, payments and outstanding balances for a specific date.",
        icon: <CalendarDays size={22} />,

    },
    {
        reportType: "INDIVIDUAL_CUSTOMER",
        reportName: "Customer Sales Credit Report",
        description: "Invoice history, paid amounts, balances and aging details for a specific customer.",
        icon: <UserCheck size={22} />,
        params: [
            {
                key: "customerId",
                label: "Customer Name",
                type: "select",
                required: true,
                options: [], // populated dynamically
            },
        ],
    },
];

// ── Other existing reports ────────────────────────────────────────────
const GENERAL_REPORTS = [
    {
        reportType: "DAILY_SUMMARY",
        reportName: "Daily Activity Summary",
        description: "Full summary of all transactions and activity for a selected date.",
        icon: <BarChart2 size={22} />,
        params: [
            {
                key: "date",
                label: "Report Date",
                type: "date",
                required: true,
                defaultValue: today,
            },
        ],
    },
    {
        reportType: "MONTHLY_SUMMARY",
        reportName: "Monthly Business Summary Report",
        description: "Orders count, gross & approved sales, cash/cheque collections, supplier payments, and estimated net profit for a selected date range.",
        icon: <CalendarRange size={22} />,
        params: [
            {
                key: "startDate",
                label: "From Date",
                type: "date",
                required: true,
                defaultValue: firstOfMonth,
            },
            {
                key: "endDate",
                label: "To Date",
                type: "date",
                required: true,
                defaultValue: lastOfMonth,
            },
        ],
    },
];

const INVENTORY_REPORTS = [
    {
        reportType: "LOW_STOCK",
        reportName: "Inventory Low Stock Alert",
        description: "Lists all products whose stock falls below the minimum threshold.",
        icon: <Package size={22} />,
        params: [],
    },
    {
        reportType: "STOCK_MOVEMENT",
        reportName: "Stock In/Out Movement Log",
        description: "All stock-in and stock-out movements within a date range.",
        icon: <Layers size={22} />,
        params: [
            {
                key: "startDate",
                label: "From Date",
                type: "date",
                required: true,
                defaultValue: firstOfMonth,
            },
            {
                key: "endDate",
                label: "To Date",
                type: "date",
                required: true,
                defaultValue: lastOfMonth,
            },
        ],
    },
    {
        reportType: "STOCK_MOVEMENT_PRODUCT_WISE",
        reportName: "Stock In/Out Movement Log – Product Wise",
        description: "Stock-in and stock-out movements grouped by product with running balance, filtered by date range.",
        icon: <Layers size={22} />,
        params: [
            {
                key: "start",
                label: "From Date",
                type: "date",
                required: true,
                defaultValue: firstOfMonth,
            },
            {
                key: "end",
                label: "To Date",
                type: "date",
                required: true,
                defaultValue: lastOfMonth,
            },
            {
                key: "productId",
                label: "Product",
                type: "select",
                required: true,
                options: [], // populated dynamically
            },
        ],
    },
    {
        reportType: "STOCK_VALUE",
        reportName: "Inventory Stock Value Report",
        description: "Current stock value for all products — item code, unit, available balance, purchase price and total value.",
        icon: <DollarSign size={22} />,
        params: [],
    },
    {
        reportType: "BATCH_STOCK",
        reportName: "Batch-wise Inventory Breakdown",
        description: "Current stock levels broken down by batch for all products.",
        icon: <LayoutList size={22} />,
        params: [],
    },
    {
        reportType: "EXPIRY_REPORT",
        reportName: "Product Expiry / Near-Expiry Report",
        description: "Products that are expired or expiring within the specified number of months. Enter 0 for already-expired only.",
        icon: <Timer size={22} />,
        params: [
            {
                key: "months",
                label: "Expiring within (months)",
                type: "number",
                required: false,
                placeholder: "e.g. 3  (0 = already expired)",
                defaultValue: "3",
            },
        ],
    },
    {
        reportType: "STOCK_STATUS",
        reportName: "Inventory Stock Status Report",
        description: "Full stock status for all products, or filter to out-of-stock items only.",
        icon: <Package size={22} />,
        params: [
            {
                key: "type",
                label: "Filter",
                type: "select",
                required: false,
                defaultValue: "",
                options: [
                    { label: "All Products", value: "" },
                    { label: "Out of Stock Only", value: "OUT_OF_STOCK" },
                ],
            },
        ],
    },
];

const ORDER_REPORTS = [
    {
        reportType: "ORDER_DETAILS",
        reportName: "Order Item Specification",
        description: "Detailed item-level breakdown for a specific sales order.",
        icon: <FileText size={22} />,
        params: [
            {
                key: "orderId",
                label: "Invoice Number",
                type: "paginated-select",
                required: true,
                placeholder: "Select an invoice...",
                pageSize: 20,
                // loadOptions is injected dynamically in the Reports component below
                loadOptions: null,
            },
        ],
    },
    {
        reportType: "ORDER_REPORT",
        reportName: "Order Summary",
        description: "Summary of sales orders, optionally filtered by status.",
        icon: <ClipboardList size={22} />,
        params: [
            {
                key: "status",
                label: "Order Status",
                type: "select",
                required: false,
                defaultValue: "",
                options: [
                    { label: "All Statuses", value: "" },
                    { label: "Pending", value: "PENDING" },
                    { label: "Approved", value: "APPROVED" },
                    { label: "Rejected", value: "REJECTED" },
                ],
            },
        ],
    },
];

const CUSTOMER_REPORTS = [
    {
        reportType: "PHARMACY_LIST",
        reportName: "Registered Pharmacies",
        description: "A full list of all registered pharmacy customers.",
        icon: <Users size={22} />,
        params: [],
    },
    {
        reportType: "TOP_CUSTOMER_LIST",
        reportName: "Top Customers",
        description: "Ranks customers by total purchase value over all time.",
        icon: <Star size={22} />,
        params: [],
    },
    {
        reportType: "PAYMENT_HISTORY",
        reportName: "Customer Payment History",
        description: "Detailed log of all customer payments within a date range.",
        icon: <Clock size={22} />,
        params: [
            {
                key: "startDate",
                label: "From Date",
                type: "date",
                required: true,
                defaultValue: firstOfMonth,
            },
            {
                key: "endDate",
                label: "To Date",
                type: "date",
                required: true,
                defaultValue: lastOfMonth,
            },
        ],
    },
    {
        reportType: "CUSTOMER_BALANCE",
        reportName: "Customer Credit Balance Summary",
        description: "Current credit balance and outstanding amounts for all customers.",
        icon: <CreditCard size={22} />,
        params: [], // no params — returns all customers
    },
    {
        reportType: "OUTSTANDING_REPORT",
        reportName: "Pending Invoice Outstanding Report",
        description: "All pending/unapproved invoices with outstanding balances.",
        icon: <AlertCircle size={22} />,
        params: [], // no params — returns live outstanding data
    },
];

const FINANCIAL_REPORTS = [
    {
        reportType: "PROFIT_LOSS",
        reportName: "Profit & Loss Statement",
        description: "Summarises total revenue, total expenses, and net operating profit for a selected period.",
        icon: <TrendingUp size={22} />,
        params: [
            { key: "startDate", label: "From Date", type: "date", required: true, defaultValue: firstOfMonth },
            { key: "endDate", label: "To Date", type: "date", required: true, defaultValue: lastOfMonth },
        ],
    },
    {
        reportType: "EXPENSE_REPORT",
        reportName: "Expense Report (GRN Payments)",
        description: "Chronological list of all GRN-related payments (cash outflows) for the selected date range.",
        icon: <Receipt size={22} />,
        params: [
            { key: "startDate", label: "From Date", type: "date", required: true, defaultValue: firstOfMonth },
            { key: "endDate", label: "To Date", type: "date", required: true, defaultValue: lastOfMonth },
        ],
    },
    {
        reportType: "CASH_FLOW_DETAILED",
        reportName: "Detailed Cash Flow Report",
        description: "Chronological list of every money movement (inflows and outflows) for the selected date range.",
        icon: <DollarSign size={22} />,
        params: [
            { key: "startDate", label: "From Date", type: "date", required: true, defaultValue: firstOfMonth },
            { key: "endDate", label: "To Date", type: "date", required: true, defaultValue: lastOfMonth },
        ],
    },
    {
        reportType: "DAILY_CASH_BALANCE",
        reportName: "Daily Cash Balance Report",
        description: "Opening balance, today's inflow/outflow, and closing balance for a specific day.",
        icon: <CalendarDays size={22} />,
        params: [
            { key: "date", label: "Date", type: "date", required: true, defaultValue: today },
        ],
    },
    {
        reportType: "CHEQUE_SUMMARY",
        reportName: "Cheque Summary Report",
        description: "All cheque transactions within a date range, optionally filtered by status (Realizing, Realized, Returned).",
        icon: <CreditCard size={22} />,
        params: [
            { key: "startDate", label: "From Date", type: "date", required: true, defaultValue: firstOfMonth },
            { key: "endDate", label: "To Date", type: "date", required: true, defaultValue: lastOfMonth },
            {
                key: "status",
                label: "Status",
                type: "select",
                required: false,
                defaultValue: "",
                options: [
                    { label: "All Statuses", value: "" },
                    { label: "Realizing", value: "REALIZING" },
                    { label: "Realized", value: "REALIZED" },
                    { label: "Returned", value: "RETURNED" },
                ],
            },
        ],
    },
    {
        reportType: "PENDING_CHEQUE_REPORT",
        reportName: "Pending Cheque Summary",
        description: "Cheques filtered by status — defaults to Realizing (pending deposit). Filter to see Realized or Returned cheques.",
        icon: <CreditCard size={22} />,
        params: [
            {
                key: "status",
                label: "Status",
                type: "select",
                required: false,
                defaultValue: "REALIZING",
                options: [
                    { label: "Realizing (Pending)", value: "REALIZING" },
                    { label: "Realized", value: "REALIZED" },
                    { label: "Returned", value: "RETURNED" },
                ],
            },
        ],
    },
];

const RETURNS_REPORTS = [
    {
        reportType: "SALES_RETURN",
        reportName: "Sales Return Report",
        description: "Total value of products returned by customers within the selected date range.",
        icon: <RotateCcw size={22} />,
        params: [
            { key: "startDate", label: "From Date", type: "date", required: true, defaultValue: firstOfMonth },
            { key: "endDate", label: "To Date", type: "date", required: true, defaultValue: lastOfMonth },
        ],
    },
    {
        reportType: "DAMAGED_ITEMS",
        reportName: "Damaged Items Report",
        description: "Lists all returned items marked as non-reusable (damaged/unsellable) within the date range.",
        icon: <ShieldAlert size={22} />,
        params: [
            { key: "startDate", label: "From Date", type: "date", required: true, defaultValue: firstOfMonth },
            { key: "endDate", label: "To Date", type: "date", required: true, defaultValue: lastOfMonth },
        ],
    },
    {
        reportType: "EXPIRY_REPORT",
        reportName: "Expired Items Report",
        description: "Products that are expired or expiring within the specified number of months. Enter 0 for already-expired only.",
        icon: <Timer size={22} />,
        params: [
            {
                key: "months",
                label: "Expiring within (months)",
                type: "number",
                required: false,
                placeholder: "e.g. 3  (0 = already expired)",
                defaultValue: "3",
            },
        ],
    },
];

// ── Section: Supplier Reports ─────────────────────────────────────────
const SUPPLIER_REPORTS = [
    {
        reportType: "SUPPLIER_GRN",
        reportName: "Supplier GRN Report",
        description: "All GRN invoices per supplier with invoice number and value for the selected date range.",
        icon: <Truck size={22} />,
        params: [
            {
                key: "start",
                label: "From Date",
                type: "date",
                required: true,
                defaultValue: firstOfMonth,
            },
            {
                key: "end",
                label: "To Date",
                type: "date",
                required: true,
                defaultValue: lastOfMonth,
            },
        ],
    },
    {
        reportType: "SUPPLIER_CREDIT_SUMMARY",
        reportName: "Supplier Credit Details Summary",
        description: "Invoice history, paid amounts, balances and aging details for a specific supplier.",
        icon: <Truck size={22} />,
        params: [
            {
                key: "supplierId",
                label: "Supplier Name",
                type: "select",
                required: true,
                options: [], // populated dynamically
            },
        ],
    },
];

const ALL_REPORTS_COUNT =
    SALES_REPORTS.length +
    GENERAL_REPORTS.length +
    INVENTORY_REPORTS.length +
    ORDER_REPORTS.length +
    CUSTOMER_REPORTS.length +
    SUPPLIER_REPORTS.length +
    FINANCIAL_REPORTS.length +
    RETURNS_REPORTS.length;

/* ── Collapsible section ── */
const Section = ({ title, reports }) => {
    const [open, setOpen] = useState(true);
    return (
        <section className="rp-section">
            <button className="rp-section-toggle" onClick={() => setOpen((o) => !o)}>
                <span>{title}</span>
                <ChevronDown
                    size={18}
                    className={`rp-chevron ${open ? "open" : ""}`}
                />
            </button>
            {open && (
                <div className="rp-grid">
                    {reports.map((r, i) => (
                        <ReportDownloader key={`${r.reportType}-${i}`} {...r} />
                    ))}
                </div>
            )}
        </section>
    );
};

/* ── Page ── */
const Reports = () => {
    const [isSidebarCollapsed, setIsSidebarCollapsed] = useState(false);
    const [isMobileSidebarOpen, setIsMobileSidebarOpen] = useState(false);
    const [customers, setCustomers] = useState([]);
    const [products, setProducts] = useState([]);
    const [suppliers, setSuppliers] = useState([]);

    useEffect(() => {
        getAllCustomers()
            .then(res => {
                const raw = res.data;
                // API returns { status, message, customers: [], timestamp }
                const list = Array.isArray(raw?.customers)
                    ? raw.customers
                    : Array.isArray(raw)
                        ? raw
                        : Array.isArray(raw?.content)
                            ? raw.content
                            : [];
                setCustomers(list);
            })
            .catch(() => { });

        getAllProducts()
            .then(res => {
                const raw = res.data;
                const list = Array.isArray(raw?.products)
                    ? raw.products
                    : Array.isArray(raw)
                        ? raw
                        : Array.isArray(raw?.content)
                            ? raw.content
                            : [];
                setProducts(list);
            })
            .catch(() => { });

        getAllSuppliers()
            .then(res => {
                const raw = res.data;
                const list = Array.isArray(raw?.suppliers)
                    ? raw.suppliers
                    : Array.isArray(raw)
                        ? raw
                        : Array.isArray(raw?.content)
                            ? raw.content
                            : [];
                setSuppliers(list);
            })
            .catch(() => { });
    }, []);

    // label = customer name shown to user; value = numeric ID sent to backend
    const customerOptions = useMemo(() => [
        { label: "--- Select Customer ---", value: "" },
        ...(Array.isArray(customers) ? customers : []).map(c => ({
            label: c.address ? `${c.name} - ${c.address}` : c.name,
            value: String(c.id),
        })),
    ], [customers]);

    // label = product name shown to user; value = numeric ID sent to backend
    const productOptions = useMemo(() => [
        { label: "--- Select Product ---", value: "" },
        ...(Array.isArray(products) ? products : []).map(p => ({ label: p.name, value: String(p.id) })),
    ], [products]);

    // inject dynamic options only into the INDIVIDUAL_CUSTOMER card
    const salesReportsWithCustomers = useMemo(() =>
        SALES_REPORTS.map(r =>
            r.reportType === "INDIVIDUAL_CUSTOMER"
                ? { ...r, params: r.params.map(p => p.key === "customerId" ? { ...p, options: customerOptions } : p) }
                : r
        ),
        [customerOptions]);

    // inject dynamic product options into the STOCK_MOVEMENT_PRODUCT_WISE card
    const inventoryReportsWithProducts = useMemo(() =>
        INVENTORY_REPORTS.map(r =>
            r.reportType === "STOCK_MOVEMENT_PRODUCT_WISE"
                ? { ...r, params: r.params.map(p => p.key === "productId" ? { ...p, options: productOptions } : p) }
                : r
        ),
        [productOptions]);

    // label = supplier name shown to user; value = numeric ID sent to backend
    const supplierOptions = useMemo(() => [
        { label: "--- Select Supplier ---", value: "" },
        ...(Array.isArray(suppliers) ? suppliers : []).map(s => ({ label: s.name, value: String(s.id) })),
    ], [suppliers]);

    // inject dynamic supplier options into the SUPPLIER_CREDIT_SUMMARY card
    const supplierReportsWithSuppliers = useMemo(() =>
        SUPPLIER_REPORTS.map(r =>
            r.reportType === "SUPPLIER_CREDIT_SUMMARY"
                ? { ...r, params: r.params.map(p => p.key === "supplierId" ? { ...p, options: supplierOptions } : p) }
                : r
        ),
        [supplierOptions]);

    // loadOptions function for the ORDER_DETAILS paginated-select
    // Returns { options: [{label, value}], hasMore } for a given page
    const loadSalesOrderOptions = useCallback(async (page, size) => {
        const res = await getPaginatedSalesOrders(page, size);
        const raw = res.data;
        // API returns { salesOrderList: [], totalPages, ... }
        const list = Array.isArray(raw?.salesOrderList)
            ? raw.salesOrderList
            : Array.isArray(raw?.content)
                ? raw.content
                : Array.isArray(raw?.salesOrders)
                    ? raw.salesOrders
                    : Array.isArray(raw)
                        ? raw
                        : [];
        const totalPages = raw?.totalPages ?? 1;
        const options = list.map(order => ({
            label: order.invoiceNumber || order.invoice_number || `#${order.id}`,
            value: String(order.id),
        }));
        return { options, hasMore: page < totalPages - 1 };
    }, []);

    // inject loadOptions into the ORDER_DETAILS card
    const orderReportsWithInvoices = useMemo(() =>
        ORDER_REPORTS.map(r =>
            r.reportType === "ORDER_DETAILS"
                ? { ...r, params: r.params.map(p => p.key === "orderId" ? { ...p, loadOptions: loadSalesOrderOptions } : p) }
                : r
        ),
        [loadSalesOrderOptions]);

    return (
        <Layout>
            <div
                className={`dashboard-container ${isSidebarCollapsed ? "sidebar-collapsed" : ""
                    }`}
            >
                <Sidebar
                    isCollapsed={isSidebarCollapsed}
                    toggleSidebar={() => setIsSidebarCollapsed(!isSidebarCollapsed)}
                    isMobileOpen={isMobileSidebarOpen}
                    toggleMobileSidebar={() =>
                        setIsMobileSidebarOpen(!isMobileSidebarOpen)
                    }
                />

                <div className="dashboard-content">
                    <div className="rp-page">

                        {/* ── Page header ── */}
                        <header className="rp-header">
                            <div className="rp-header-left">
                                <div className="rp-header-icon">
                                    <BarChart2 size={24} />
                                </div>
                                <div>
                                    <h1 className="rp-title">Reports</h1>
                                    <p className="rp-subtitle">
                                        Generate and download business intelligence reports as PDF
                                    </p>
                                </div>
                            </div>
                            <div className="rp-badge">
                                {ALL_REPORTS_COUNT} reports available
                            </div>
                        </header>

                        {/* ── Report sections ── */}
                        <Section title="📈 Sales Reports (Approved Orders)" reports={salesReportsWithCustomers} />
                        <Section title="📊 General Reports" reports={GENERAL_REPORTS} />
                        <Section title="📦 Inventory Reports" reports={inventoryReportsWithProducts} />
                        <Section title="🗂️ Order Reports" reports={orderReportsWithInvoices} />
                        <Section title="👥 Customer Reports" reports={CUSTOMER_REPORTS} />
                        <Section title="🚚 Supplier Reports" reports={supplierReportsWithSuppliers} />
                        <Section title="💰 Financial Reports" reports={FINANCIAL_REPORTS} />
                        <Section title="🔄 Returns & Damage Reports" reports={RETURNS_REPORTS} />
                    </div>
                </div>
            </div>
        </Layout>
    );
};

export default Reports;
